import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PenSquare, FileText, Inbox, Eye, TrendingUp, MessageCircle, PlusCircle, Settings as SettingsIcon } from "lucide-react";
import { api } from "../../api";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ posts: 0, pages: 0, submissions: 0, unread: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, pg, sb] = await Promise.all([
          api.get("/admin/blog"),
          api.get("/admin/pages"),
          api.get("/admin/submissions"),
        ]);
        setStats({
          posts: p.data.length,
          pages: pg.data.length,
          submissions: sb.data.length,
          unread: sb.data.filter((x) => !x.read).length,
        });
        setRecent(sb.data.slice(0, 5));
      } catch (e) { console.error(e); }
    })();
  }, []);

  const cards = [
    { icon: PenSquare, label: "Blog Posts", value: stats.posts, to: "/admin/posts", color: "from-amber-400 to-amber-600" },
    { icon: FileText, label: "Custom Pages", value: stats.pages, to: "/admin/pages", color: "from-sky-400 to-sky-600" },
    { icon: Inbox, label: "Submissions", value: stats.submissions, to: "/admin/submissions", color: "from-emerald-400 to-emerald-600" },
    { icon: TrendingUp, label: "Unread", value: stats.unread, to: "/admin/submissions", color: "from-rose-400 to-rose-600" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Dashboard</h1>
          <p className="text-slate-400 text-sm mt-1">Manage your site content, blog posts, and SEO settings.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/posts/new" className="btn-gold inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm"><PlusCircle className="w-4 h-4" /> New Post</Link>
          <Link to="/admin/pages/new" className="btn-outline-gold inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm"><PlusCircle className="w-4 h-4" /> New Page</Link>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Link to={c.to} key={c.label} className="card-dark p-5 group">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center text-[#0a1226]`}><c.icon className="w-5 h-5" /></div>
            <div className="mt-3 text-3xl font-extrabold">{c.value}</div>
            <div className="text-xs text-slate-400 uppercase tracking-wide mt-1">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="card-dark p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold">Recent Submissions</h2>
            <Link to="/admin/submissions" className="text-amber-400 text-xs hover:text-amber-300">View all →</Link>
          </div>
          {recent.length === 0 ? (
            <div className="text-slate-400 text-sm py-8 text-center">No submissions yet</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {recent.map((s) => (
                <li key={s.id} className="py-3 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-300 text-sm font-bold">{(s.name || "?")[0]?.toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{s.name} <span className="text-slate-500 text-xs">· {s.email}</span></div>
                    <div className="text-xs text-slate-400 truncate">{s.message}</div>
                  </div>
                  {!s.read && <span className="text-[10px] bg-rose-500/20 text-rose-300 px-2 py-0.5 rounded-full">NEW</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-dark p-5">
          <h2 className="font-bold mb-3">Quick Links</h2>
          <div className="grid grid-cols-2 gap-2.5">
            <Link to="/admin/settings" className="p-3 rounded-lg bg-white/5 hover:bg-amber-500/10 text-sm flex items-center gap-2"><SettingsIcon className="w-4 h-4 text-amber-400" /> Site Settings</Link>
            <Link to="/admin/settings?tab=seo" className="p-3 rounded-lg bg-white/5 hover:bg-amber-500/10 text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-amber-400" /> SEO</Link>
            <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="p-3 rounded-lg bg-white/5 hover:bg-amber-500/10 text-sm flex items-center gap-2"><Eye className="w-4 h-4 text-amber-400" /> View sitemap.xml</a>
            <a href="/api/robots.txt" target="_blank" rel="noreferrer" className="p-3 rounded-lg bg-white/5 hover:bg-amber-500/10 text-sm flex items-center gap-2"><Eye className="w-4 h-4 text-amber-400" /> View robots.txt</a>
          </div>
          <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-200 flex items-start gap-2">
            <MessageCircle className="w-4 h-4 mt-0.5" />
            <div>Tip: Use Site Settings to change brand name, logo, WhatsApp number, and home page sections at any time.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
